/* ============================================================
   Corridor cost benchmarks — cost per ton-kilometre.

   Audit A-04: the scaffold asserted bands with no source, no date and
   no payload assumption, and cross-checking suggested the road medians
   ran 30–40% high. An operator priced at market could be told they were
   "poor" and act on it.

   Every band below now carries its derivation, the payload it assumes,
   and the date it was struck. The arithmetic is shown so a reader can
   disagree with it rather than having to trust it.

   PROVENANCE
     published-tariff — taken directly from an operator's published rate
     derived          — computed from published guidance; assumptions stated
     contributed      — median of opt-in user contributions (n shown)

   No band is `contributed` yet. Those appear once the opt-in
   contribution layer ships and a lane-month reaches n ≥ 7.
   ============================================================ */

export const DEFAULT_PAYLOAD_TONNES = 28;
export const BENCHMARK_AS_OF = '2026-08-02';

/**
 * Road bands are derived from Kenya Transporters Association rate
 * guidance: a floor of USD 2.12/km one-way for transit cargo and
 * KSh 225/km one-way for local cargo. At a 28-tonne payload that is
 * roughly USD 0.076/ton-km transit and USD 0.062/ton-km local, before
 * margin — which sets the low band. Median and high add operator margin
 * and corridor friction.
 *
 * Rail bands come straight from published KRC SGR container tariffs,
 * and cover LINE HAUL ONLY. First and last mile haulage, lift-on/
 * lift-off and ICD storage are excluded and must be added separately —
 * this is the single most common error when comparing rail to road.
 */
export const BENCHMARKS = {
  'mombasa-nairobi': {
    name: 'Mombasa — Nairobi',
    distance: 485,
    currency: 'USD',
    modes: {
      road: {
        low: 0.055,
        median: 0.07,
        high: 0.095,
        provenance: 'derived',
        basis:
          'KTA local-cargo guidance of KSh 225/km one-way ≈ USD 0.062/ton-km at 28 t, ' +
          'plus operator margin. Independently corroborated: reported spot rates of KSh ' +
          '80,000–100,000 for a 20 ft box over this lane give KSh 90,000 ÷ 485 km ÷ 20 t ≈ ' +
          'USD 0.072/ton-km, landing on the median above. Two sources, different units, ' +
          'same answer.',
        payloadTonnes: 28
      },
      rail: {
        low: 0.024,
        median: 0.028,
        high: 0.035,
        provenance: 'published-tariff',
        basis:
          'KRC SGR container tariff: 20ft USD 250, 40ft USD 350 over 485 km. ' +
          'USD 250 ÷ (20 t × 485 km) ≈ USD 0.026/ton-km. Line haul only — ' +
          'excludes first/last mile, lift charges and ICD storage.',
        payloadTonnes: 20
      }
    },
    asOf: BENCHMARK_AS_OF,
    source: 'KRC SGR published container tariff; Kenya Transporters Association rate guidance',
    notes:
      'Rail line-haul is materially cheaper per ton-km than road on this lane. The comparison ' +
      'only holds once drayage at both ends is added to the rail figure.'
  },

  'mombasa-naivasha': {
    name: 'Mombasa — Naivasha ICD',
    distance: 520,
    currency: 'USD',
    modes: {
      road: {
        low: 0.058,
        median: 0.075,
        high: 0.1,
        provenance: 'derived',
        basis: 'As Mombasa–Nairobi, adjusted for distance and inland grade.',
        payloadTonnes: 28
      },
      rail: {
        low: 0.023,
        median: 0.027,
        high: 0.034,
        provenance: 'published-tariff',
        basis:
          'KRC SGR tariff: Naivasha–Mombasa 20ft USD 255 over 520 km. ' +
          'USD 255 ÷ (20 t × 520 km) ≈ USD 0.025/ton-km. Line haul only.',
        payloadTonnes: 20
      }
    },
    asOf: BENCHMARK_AS_OF,
    source: 'KRC SGR published container tariff',
    notes: 'Naivasha ICD is the SGR inland terminus; onward road haulage is unavoidable.'
  },

  'mombasa-kampala': {
    name: 'Mombasa — Kampala',
    distance: 1200,
    currency: 'USD',
    modes: {
      road: {
        low: 0.08,
        median: 0.1,
        high: 0.135,
        provenance: 'derived',
        basis:
          'KTA transit floor of USD 2.12/km ≈ USD 0.076/ton-km at 28 t. Median of 0.10 ' +
          'implies ≈ USD 3,360 for a 28 t load, consistent with reported 40ft rates of ' +
          'around USD 3,500 on this lane.',
        payloadTonnes: 28
      }
    },
    asOf: BENCHMARK_AS_OF,
    source: 'Kenya Transporters Association rate guidance; reported Northern Corridor 40ft rates',
    notes:
      'Northern Corridor transit under the EAC customs union. Reported road transit is ' +
      'typically 4–7 days. No through-rail service; rail is not benchmarked on this lane.'
  },

  /* Central Corridor. The comparison a Rwandan or Burundian shipper
     actually agonises over, and the first non-Mombasa lane here.

     Sourced deliberately narrowly. The widely-quoted EAC route table
     ($2.50/tonne Kampala–Mombasa, $0.17 Dar–Kigali and so on) is not
     used: it reports Dar–Bujumbura at $0.02 and Bujumbura–Dar at $1.02,
     the same lane 50x apart by direction, and its Kampala–Mombasa
     figure converts to USD 0.002/tonne-km — roughly thirty times below
     the KTA-derived Northern Corridor band already in this file. The
     units in that table do not survive a sanity check, whatever the
     headline says, so it is cited nowhere.

     The EABC per-container figure does survive, and it corroborates:
     USD 1.8/km/container over a 20 t box is USD 0.090/tonne-km, which
     lands on the top of the independently-derived Mombasa–Nairobi road
     band. Two sources arriving at the same place by different routes is
     the only reason this band is here at all. */
  /* Intra-Kenya short haul. Different economics from the transit lanes,
     and the reason it is worth its own entry rather than being read off
     Mombasa–Nairobi: fixed costs per trip — loading, turnaround, empty
     repositioning — spread over 350 km instead of 485, so the realised
     cost per tonne-km sits above the published minimum rather than on
     it. The band is built from that, not from a separate rate card,
     because no lane-specific card exists. */
  /* Kampala–Juba. The only lane here priced from actual quoted truck
     hire rather than a per-km rate card, which makes it the most
     directly evidenced band in the file — and the one where the
     official rate is least of the story.

     Two payloads were quoted for the same lane, and they disagree in
     the direction basic economics predicts: the 35 t trailer comes out
     cheaper per tonne-km than the 28 t truck, because the trip cost
     spreads over more tonnes. That internal consistency is why these
     figures are trusted where the EAC route table was not. */
  /* Eastern DRC. I expected this to come back too thin to encode and it
     did not — NCTTCA, the same authority this file already leans on for
     dwell and corridor context, publishes rates for it.

     Three of their figures bracket each other, which is what earns the
     band: a stated 12 US cents per tonne-km, and two per-container-per-
     km rates that convert to 0.087 and 0.150 at a 20 t box. A single
     quoted number would have been worth less than these three
     disagreeing within a sensible range. */
  'mombasa-goma': {
    name: 'Mombasa — Goma',
    distance: 1800,
    currency: 'USD',
    modes: {
      road: {
        low: 0.087,
        median: 0.12,
        high: 0.15,
        provenance: 'derived',
        basis:
          'NCTTCA transport observatory: Mombasa to Goma stated at 12 US cents per tonne-km, ' +
          'with per-container rates of USD 1.733/km and USD 3.00/km on the same lane. Over a ' +
          '20 t box those convert to USD 0.087 and 0.150/ton-km, bracketing the stated 0.12 ' +
          'used as the median. The most expensive lane in this file, which is what the ' +
          'observatory reports for it.',
        payloadTonnes: 20
      }
    },
    asOf: '2026-08-04',
    measuredAt: '2023',
    source: 'NCTTCA Northern Corridor Transport Observatory — DR Congo road freight charges',
    notes:
      'Two limits worth reading before using this. The underlying observatory figures are ' +
      '2023 vintage; asOf records when they were last checked, not when they were measured, ' +
      'which is true of every band here but matters more on a lane this volatile. And the ' +
      'distance is nominal — Mombasa to Goma runs either via Kampala and Kasese or via ' +
      'Kigali and Gisenyi, and the two differ enough that a route-specific figure would be ' +
      'better if you have one. No rail beyond the Kenyan and Ugandan legs.'
  },

  'kampala-juba': {
    name: 'Kampala — Juba',
    distance: 640,
    currency: 'USD',
    modes: {
      road: {
        low: 0.097,
        median: 0.106,
        high: 0.114,
        provenance: 'derived',
        basis:
          'Quoted truck hire, Kampala to Juba via Nimule: UGX 7m (≈ USD 1,895) for a 28 t ' +
          'truck and UGX 8m (≈ USD 2,166) for a 35 t trailer. Over 640 km that is USD 1,895 ÷ ' +
          '(28 × 640) ≈ 0.106/ton-km and USD 2,166 ÷ (35 × 640) ≈ 0.097 — the larger payload ' +
          'cheaper per tonne-km, as it should be. The high adds the documented non-tariff ' +
          'charges on this route: a USD 40 weighbridge fee at Nimule and USD 100 of ' +
          'unreceipted roadblock payments between Nimule and Juba, which is USD 140 ÷ (28 × ' +
          '640) ≈ 0.008/ton-km on top.',
        payloadTonnes: 28
      }
    },
    asOf: '2026-08-04',
    measuredAt: '2024',
    source: 'Reported Kampala–Juba truck hire rates and documented Nimule-corridor charges',
    notes:
      'The high band includes unofficial payments because on this lane they are routine and ' +
      'documented, not exceptional — pricing the route without them would understate it for ' +
      'every operator who actually runs it. Excluded from all three figures: overload ' +
      'negotiation, reported at USD 600–2,500 per incident. That is a penalty, not a rate, ' +
      'and averaging it into a per-tonne-km band would make a compliant load look more ' +
      'expensive than it is. Use the NTB Reporter for that exposure instead. No rail: there ' +
      'is no rail link to Juba.'
  },

  'nairobi-kisumu': {
    name: 'Nairobi — Kisumu',
    distance: 350,
    currency: 'USD',
    modes: {
      road: {
        low: 0.062,
        median: 0.079,
        high: 0.107,
        provenance: 'derived',
        basis:
          'KTA quotes two floors: KSh 225/km one-way for LOCAL cargo and USD 2.12/km for ' +
          'TRANSIT. This lane is local, so KSh 225/km ÷ 28 t ≈ USD 0.062/ton-km is the low — a ' +
          'floor, not an expectation. Median and high apply the same proportional spread the ' +
          'Mombasa–Nairobi band uses (1.27x and 1.73x the low), because short-haul fixed costs ' +
          'per trip spread over fewer kilometres and push the realised rate above the minimum.',
        payloadTonnes: 28
      }
    },
    asOf: '2026-08-04',
    source: 'Kenya Transporters Association — local-cargo rate guidance',
    notes:
      'No rail band. SGR reaches Kisumu but there is no published container tariff for the ' +
      'lane to derive one from. The spread here rests on one published floor plus a stated ' +
      'method, not on two independent sources — treat the median as the weakest figure in ' +
      'this file.'
  },

  'dar-kigali': {
    name: 'Dar es Salaam — Kigali',
    distance: 1495,
    currency: 'USD',
    modes: {
      road: {
        low: 0.05,
        median: 0.09,
        high: 0.122,
        provenance: 'derived',
        basis:
          'EABC puts EAC road logistics at USD 1.8/km per container against an international ' +
          'best practice of USD 1.0/km. Over a 20 t box: 1.8 ÷ 20 = USD 0.090/tonne-km as the ' +
          'median, 1.0 ÷ 20 = USD 0.050 as a best-practice floor. The high is the median ' +
          'widened by the same proportional spread the KTA-derived Mombasa–Nairobi band uses ' +
          '(high ≈ 1.36 × median). Regional average applied to this lane, not a lane-specific ' +
          'rate — treat it as an order of magnitude, not a quote.',
        payloadTonnes: 20
      }
    },
    asOf: '2026-08-04',
    source: 'East African Business Council — EAC transport and logistics cost per km per container',
    notes:
      'No rail band. The Central Corridor SGR is under construction and there is no published ' +
      'through tariff to Kigali to derive one from; an empty entry is more honest than a ' +
      'modelled one. Road figures here are a regional average, so they carry more uncertainty ' +
      'than the Northern Corridor lanes, which come from lane-specific guidance.'
  },

  'mombasa-kigali': {
    name: 'Mombasa — Kigali',
    distance: 1650,
    currency: 'USD',
    modes: {
      road: {
        low: 0.09,
        median: 0.11,
        high: 0.15,
        provenance: 'derived',
        basis:
          'Transit floor plus additional border friction at Malaba/Busia and Gatuna. ' +
          'Median of 0.11 implies ≈ USD 5,080 for a 28 t load.',
        payloadTonnes: 28
      }
    },
    asOf: BENCHMARK_AS_OF,
    source: 'Kenya Transporters Association rate guidance; NCTTCA corridor reporting',
    notes:
      'Reported transit is 7–10 days, targeted to fall to 5–6 days as corridor works ' +
      'complete. Two border crossings dominate the variance, not distance.'
  },

  'nairobi-mombasa': {
    name: 'Nairobi — Mombasa (backhaul)',
    distance: 485,
    currency: 'USD',
    modes: {
      road: {
        low: 0.04,
        median: 0.052,
        high: 0.072,
        provenance: 'derived',
        basis:
          'Backhaul typically prices 20–30% below the outbound leg because the ' +
          'alternative is running empty. Derived from the Mombasa–Nairobi road band.',
        payloadTonnes: 28
      },
      rail: {
        low: 0.024,
        median: 0.028,
        high: 0.035,
        provenance: 'published-tariff',
        basis: 'KRC SGR tariff is directionally symmetric on this lane.',
        payloadTonnes: 20
      }
    },
    asOf: BENCHMARK_AS_OF,
    source: 'KRC SGR published container tariff; Kenya Transporters Association rate guidance',
    notes:
      'A backhaul rate below the outbound band is normal and not a sign of underpricing — ' +
      'the relevant comparison is against running empty.'
  }
};

export function getBenchmark(corridorKey) {
  return Object.prototype.hasOwnProperty.call(BENCHMARKS, corridorKey)
    ? BENCHMARKS[corridorKey]
    : null;
}

export function getAllCorridors() {
  return Object.entries(BENCHMARKS).map(([key, data]) => ({ key, ...data }));
}

/** Modes with a published band on this corridor. */
export function availableModes(corridorKey) {
  const bench = getBenchmark(corridorKey);
  return bench ? Object.keys(bench.modes) : [];
}

export function getBand(corridorKey, mode) {
  const bench = getBenchmark(corridorKey);
  if (!bench) return null;
  return Object.prototype.hasOwnProperty.call(bench.modes, mode) ? bench.modes[mode] : null;
}

/**
 * Compare a computed cost against the band.
 * Audit A-05: returns an explicit reason when no band exists, so the UI
 * can say why rather than rendering an empty result as if it were one.
 */
export function compareToBenchmark(corridorKey, mode, costPerTonKm) {
  const bench = getBenchmark(corridorKey);
  if (!bench) {
    return { available: false, reason: 'No benchmark exists for this corridor.' };
  }

  const band = getBand(corridorKey, mode);
  if (!band) {
    const modes = availableModes(corridorKey);
    return {
      available: false,
      reason:
        modes.length > 0
          ? `No published band for ${mode} on ${bench.name}. Benchmarked modes: ${modes.join(', ')}.`
          : `No published band for ${bench.name}.`
    };
  }

  if (!Number.isFinite(costPerTonKm) || costPerTonKm <= 0) {
    return { available: false, reason: 'Enter a distance, payload and at least one cost.' };
  }

  const { low, median, high } = band;
  let status, tone;

  if (costPerTonKm <= low) {
    status = 'excellent';
    tone = 'var(--ca-positive)';
  } else if (costPerTonKm <= median) {
    status = 'good';
    tone = 'var(--ca-chart-2)';
  } else if (costPerTonKm <= high) {
    status = 'fair';
    tone = 'var(--ca-warning)';
  } else {
    status = 'poor';
    tone = 'var(--ca-danger)';
  }

  return { available: true, status, tone, low, median, high, band, corridor: bench };
}

/**
 * Marker position on the gauge, 0–100.
 *
 * Audit A-15: the previous gauge interpolated linearly between low and
 * high while the status labels were keyed to low/median/high — so a cost
 * exactly at median could render left of centre while reading "good".
 * Median is now pinned to the midpoint and each half interpolated
 * separately, so the marker always agrees with its label.
 */
/**
 * What the distance to the band is worth in money.
 *
 * The tools already show where an operator sits — low, median, high, and
 * a status word. What none of them said is what closing that distance is
 * worth, and "you are fair" does not tell anyone whether the answer is a
 * phone call to a fuel supplier or a quarter of restructuring.
 *
 * Two targets rather than one, deliberately. A single "you could save X"
 * implies a precision the band does not have: the median is what a
 * competent operator on this lane achieves, the low end is what the best
 * do, and the gap between those two answers is itself the useful
 * information. Below the median this returns null — inventing a saving
 * for someone already beating the market would be the same false
 * precision pointed the other way.
 *
 * @param {number} costPerTonKm the operator's own figure
 * @param {object} band         low / median / high
 * @param {number} tonneKm      the unit of work the saving applies to
 */
export function bandGap(costPerTonKm, band, tonneKm) {
  if (!band || !Number.isFinite(costPerTonKm) || costPerTonKm <= 0) return null;
  if (!Number.isFinite(tonneKm) || tonneKm <= 0) return null;
  if (costPerTonKm <= band.median) return null;

  return {
    toMedian: (costPerTonKm - band.median) * tonneKm,
    toLow: (costPerTonKm - band.low) * tonneKm,
    perTonneKmToMedian: costPerTonKm - band.median,
    perTonneKmToLow: costPerTonKm - band.low
  };
}

export function gaugePosition(costPerTonKm, band) {
  if (!band || !Number.isFinite(costPerTonKm)) return 0;
  const { low, median, high } = band;

  if (costPerTonKm <= low) return 0;
  if (costPerTonKm >= high) return 100;

  if (costPerTonKm <= median) {
    const span = median - low;
    return span > 0 ? ((costPerTonKm - low) / span) * 50 : 25;
  }

  const span = high - median;
  return span > 0 ? 50 + ((costPerTonKm - median) / span) * 50 : 75;
}
